import { z } from 'zod';
import {
  phoneValidator,
  birthDateValidator,
  countryValidator,
  SalutationEnum,
  GenderEnum,
  nationalityValidator,
  calculateAge,
  ADULT_MIN_AGE,
  CHILD_MAX_AGE,
} from './validation';
import { parseGermanDateLocal } from '@/utils/formatting/dateParser';

// ===========================
// Form Validation Schemas (for vee-validate)
// These schemas exclude metadata fields (id, type) that aren't user-editable
// ===========================

/**
 * Adult Traveler Form Schema (for validation only)
 * Excludes metadata fields (id, type) that aren't user-editable.
 * Use this schema with vee-validate's useForm() for form validation.
 */
export const AdultTravelerFormSchema = z.object({
  salutation: SalutationEnum,
  birthDate: birthDateValidator.refine((date) => {
    const age = calculateAge(parseGermanDateLocal(date));
    return age >= ADULT_MIN_AGE;
  }, 'ibe.validation.traveler.adult-age-minimum'),
  firstName: z
    .string()
    .min(1, 'ibe.validation.firstName.required')
    .max(100, 'ibe.validation.firstName.too-long'),
  lastName: z
    .string()
    .min(1, 'ibe.validation.lastName.required')
    .max(100, 'ibe.validation.lastName.too-long'),
  // --- Address & contact fields for ADDITIONAL travelers (street, postalCode, location,
  //     country, mobile, email) are temporarily hidden in the UI.
  //     The booking/contact person still collects these (see contact.ts).
  //     They are kept here as OPTIONAL so existing data plumbing (storage/restore and the
  //     DaVinci participant mapping) continues to type-check and the form stays submittable.
  //     To restore: re-enable the required validators below (and the matching inputs in
  //     CheckoutTravelerForm.vue) and remove the .optional() versions. ---
  // street: z
  //   .string()
  //   .min(1, 'ibe.validation.street.required')
  //   .max(200, 'ibe.validation.street.too-long'),
  street: z.string().max(200, 'ibe.validation.street.too-long').optional(),
  // postalCode: z
  //   .string()
  //   .min(1, 'ibe.validation.postalCode.required')
  //   .regex(/^\d{4,10}$/, 'ibe.validation.postalCode.invalid'),
  // .or(z.literal('')) tolerates an empty string (e.g. restored from a pre-change
  // session) so the hidden field can never produce an unfixable validation error.
  postalCode: z
    .string()
    .regex(/^\d{4,10}$/, 'ibe.validation.postalCode.invalid')
    .optional()
    .or(z.literal('')),
  // location: z
  //   .string()
  //   .min(1, 'ibe.validation.location.required')
  //   .max(100, 'ibe.validation.location.too-long'),
  location: z.string().max(100, 'ibe.validation.location.too-long').optional(),
  // country: countryValidator,
  country: countryValidator.optional().or(z.literal('')),
  nationality: nationalityValidator,
  // mobile: phoneValidator,
  mobile: phoneValidator.optional().or(z.literal('')),
  // email: z.string().min(1, 'ibe.validation.email.required').email('ibe.validation.email.invalid'),
  email: z.string().email('ibe.validation.email.invalid').optional().or(z.literal('')),
});

/**
 * Child Traveler Form Schema (for validation only)
 * Excludes metadata fields (id, type) that aren't user-editable.
 * Use this schema with vee-validate's useForm() for form validation.
 */
export const ChildTravelerFormSchema = z.object({
  gender: GenderEnum,
  firstName: z
    .string()
    .min(1, 'ibe.validation.firstName.required')
    .max(100, 'ibe.validation.firstName.too-long'),
  lastName: z
    .string()
    .min(1, 'ibe.validation.lastName.required')
    .max(100, 'ibe.validation.lastName.too-long'),
  birthDate: birthDateValidator.refine((date) => {
    const age = calculateAge(parseGermanDateLocal(date));
    return age <= CHILD_MAX_AGE;
  }, 'ibe.validation.traveler.child-age-maximum'),
  nationality: nationalityValidator,
});

// Form value types (what vee-validate works with)
export type AdultTravelerFormValues = z.infer<typeof AdultTravelerFormSchema>;
export type ChildTravelerFormValues = z.infer<typeof ChildTravelerFormSchema>;

// ===========================
// Full Data Schemas (for type definitions and data storage)
// These include metadata fields (id, type) added when collecting form data
//
// NOTE: The Traveler type exported here is for DATA STORAGE (id: number).
// For DISPLAY purposes, use the Traveler type from types/Traveler.ts
// which supports id: string | number (e.g., 'contact' for contact person).
// ===========================

/**
 * Adult Traveler Schema
 * Full schema including metadata fields for data storage.
 * Adult travelers (18+) require full address and contact information.
 */
export const AdultTravelerSchema = AdultTravelerFormSchema.extend({
  id: z.number(),
  type: z.literal('adult'),
});

/**
 * Child Traveler Schema
 * Full schema including metadata fields for data storage.
 * Child travelers (<18) only require basic information.
 */
export const ChildTravelerSchema = ChildTravelerFormSchema.extend({
  id: z.number(),
  type: z.literal('child'),
});

/**
 * Gender-style storage base — the shared shape for the non-adult variants
 * (under18adult, infant), mirroring the child form (gender + names + nationality).
 *
 * Unlike ChildTravelerSchema, no variant-specific age refine is baked in: the
 * per-variant age range depends on check-in + the hotel's age bands, so it is
 * enforced at the form layer (createTravelerFormSchema), not on the stored shape.
 */
const GenderStyleStorageBase = ChildTravelerFormSchema.extend({
  birthDate: birthDateValidator,
});

/**
 * Under-18 Adult Traveler Schema
 * A hotel-classified adult who is younger than 18 at check-in (the hotel says
 * "Adult", but the form collects gender like a child). Full storage shape.
 */
export const Under18AdultTravelerSchema = GenderStyleStorageBase.extend({
  id: z.number(),
  type: z.literal('under18adult'),
});

/**
 * Infant Traveler Schema
 * Full storage shape for an infant (hotel's youngest band). Gender-style.
 */
export const InfantTravelerSchema = GenderStyleStorageBase.extend({
  id: z.number(),
  type: z.literal('infant'),
});

/**
 * Traveler Schema - Discriminated Union
 * Automatically validates based on 'type' field (4-way travelerVariant)
 */
export const TravelerSchema = z.discriminatedUnion('type', [
  AdultTravelerSchema,
  ChildTravelerSchema,
  Under18AdultTravelerSchema,
  InfantTravelerSchema,
]);

export type AdultTraveler = z.infer<typeof AdultTravelerSchema>;
export type ChildTraveler = z.infer<typeof ChildTravelerSchema>;
export type Under18AdultTraveler = z.infer<typeof Under18AdultTravelerSchema>;
export type InfantTraveler = z.infer<typeof InfantTravelerSchema>;
export type Traveler = z.infer<typeof TravelerSchema>;
